import type {
    INotificationRepository,
    Notification,
    NotificationChannel,
    NotificationListFilters,
    NotificationPreferences,
    PaginatedResponse,
    RegisteredDevice,
    RegisterPushTokenDTO,
    SendBulkNotificationDTO,
    SendNotificationDTO,
    UpdatePreferencesDTO,
} from '@yawi/domain';
import { getApiClient } from '../../http/apiClient';
import { ApiError } from '../../http/ApiError';

export class PushTokenError extends Error {
  constructor(
    public readonly code: 'NOTIFICATION_12002' | 'NOTIFICATION_12003' | 'UNKNOWN',
    public readonly retryable: boolean,
    message: string,
  ) {
    super(message);
    this.name = 'PushTokenError';
  }

  static isPushTokenError(err: unknown): err is PushTokenError {
    return err instanceof PushTokenError;
  }
}

// Tipos de respuesta de la API
interface ApiNotificationDelivery {
  channel: 'EMAIL' | 'PUSH' | 'SMS' | 'WHATSAPP' | 'IN_APP';
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'FAILED';
  sentAt?: string | null;
  deliveredAt?: string | null;
  errorMessage?: string | null;
  externalId?: string | null;
}

interface ApiNotificationResponse {
  id: string;
  userId: string;
  type: string;
  title: string;
  body: string;
  data?: Record<string, unknown> | null;
  status: 'PENDING' | 'SENT' | 'PARTIAL' | 'FAILED' | 'READ';
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  readAt?: string | null;
  createdAt: string;
  deliveries: ApiNotificationDelivery[];
}

interface ApiNotificationPreferencesResponse {
  id: string;
  userId: string;
  pushEnabled: boolean;
  pushQuietStart?: string | null;
  pushQuietEnd?: string | null;
  emailEnabled: boolean;
  emailDigest: 'immediate' | 'daily' | 'weekly';
  smsEnabled: boolean;
  smsOnlyUrgent: boolean;
  whatsappEnabled: boolean;
  inAppEnabled: boolean;
  categorySettings?: Record<string, unknown> | null;
}

interface ApiUnreadCountResponse {
  count: number;
}

// Mapea la respuesta de la API al tipo Notification interno
const mapApiNotificationToNotification = (apiNotification: ApiNotificationResponse): Notification => ({
  id: apiNotification.id,
  userId: apiNotification.userId,
  type: apiNotification.type as Notification['type'],
  title: apiNotification.title,
  body: apiNotification.body,
  data: apiNotification.data || undefined,
  status: apiNotification.status,
  priority: apiNotification.priority,
  readAt: apiNotification.readAt || undefined,
  createdAt: apiNotification.createdAt,
  deliveries: (apiNotification.deliveries ?? []).map((d) => ({
    channel: d.channel,
    status: d.status,
    sentAt: d.sentAt || undefined,
    deliveredAt: d.deliveredAt || undefined,
    errorMessage: d.errorMessage || undefined,
    externalId: d.externalId || undefined,
  })),
});

// Mapea la respuesta de la API al tipo NotificationPreferences interno
const mapApiPreferencesToPreferences = (
  apiPreferences: ApiNotificationPreferencesResponse
): NotificationPreferences => ({
  id: apiPreferences.id,
  userId: apiPreferences.userId,
  pushEnabled: apiPreferences.pushEnabled,
  pushQuietStart: apiPreferences.pushQuietStart || undefined,
  pushQuietEnd: apiPreferences.pushQuietEnd || undefined,
  emailEnabled: apiPreferences.emailEnabled,
  emailDigest: apiPreferences.emailDigest,
  smsEnabled: apiPreferences.smsEnabled,
  smsOnlyUrgent: apiPreferences.smsOnlyUrgent,
  whatsappEnabled: apiPreferences.whatsappEnabled,
  inAppEnabled: apiPreferences.inAppEnabled,
  categorySettings: apiPreferences.categorySettings || undefined,
});

export class ApiNotificationRepository implements INotificationRepository {
  /**
   * Devuelve `meta` tal cual: el listado filtra y pagina en el servidor, así que
   * el contador y los botones tienen que leer el total de la respuesta y no la
   * longitud de la página.
   */
  async getNotifications(
    filters?: NotificationListFilters
  ): Promise<PaginatedResponse<Notification>> {
    const client = getApiClient();
    const params: Record<string, string | number> = {};
    if (filters?.page) params.page = filters.page;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.search) params.search = filters.search;
    if (filters?.channel) params.channel = filters.channel;
    if (filters?.status) params.status = filters.status;

    const response = await client.get<PaginatedResponse<ApiNotificationResponse>>(
      'v1/notifications',
      { params }
    );

    return {
      ...response,
      data: (response.data ?? []).map(mapApiNotificationToNotification),
    };
  }

  async getUnreadNotifications(): Promise<Notification[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiNotificationResponse>>('v1/notifications/unread');
    return response.data.map(mapApiNotificationToNotification);
  }

  async getUnreadCount(): Promise<number> {
    const client = getApiClient();
    const response = await client.get<ApiUnreadCountResponse>('v1/notifications/unread/count');
    return response.count;
  }

  async markAsRead(id: string): Promise<void> {
    const client = getApiClient();
    await client.post(`v1/notifications/${id}/read`);
  }

  async markAllAsRead(): Promise<void> {
    const client = getApiClient();
    await client.post('v1/notifications/read-all');
  }

  async getPreferences(): Promise<NotificationPreferences> {
    const client = getApiClient();
    const apiPreferences = await client.get<ApiNotificationPreferencesResponse>('v1/notifications/preferences');
    return mapApiPreferencesToPreferences(apiPreferences);
  }

  async updatePreferences(preferences: UpdatePreferencesDTO): Promise<NotificationPreferences> {
    const client = getApiClient();
    const apiPreferences = await client.put<ApiNotificationPreferencesResponse>(
      'v1/notifications/preferences',
      preferences
    );
    return mapApiPreferencesToPreferences(apiPreferences);
  }

  async registerPushToken(dto: RegisterPushTokenDTO): Promise<void> {
    const client = getApiClient();
    try {
      await client.post('v1/notifications/push-token', dto);
    } catch (err: unknown) {
      if (ApiError.isApiError(err)) {
        if (err.code === 'NOTIFICATION_12002') {
          throw new PushTokenError('NOTIFICATION_12002', false, err.message);
        }
        if (err.code === 'NOTIFICATION_12003') {
          throw new PushTokenError('NOTIFICATION_12003', true, err.message);
        }
      }
      throw err;
    }
  }

  async unregisterPushToken(deviceId: string): Promise<void> {
    const client = getApiClient();
    await client.delete('v1/notifications/push-token', { body: { deviceId } });
  }

  async getDevices(): Promise<RegisteredDevice[]> {
    const client = getApiClient();
    return client.get<RegisteredDevice[]>('v1/notifications/devices');
  }

  async removeDevice(deviceId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/notifications/devices/${deviceId}`);
  }

  async sendNotification(notification: SendNotificationDTO): Promise<void> {
    const client = getApiClient();
    await client.post('v1/notifications/send', notification);
  }

  async sendBulkNotification(notification: SendBulkNotificationDTO): Promise<void> {
    const client = getApiClient();
    await client.post('v1/notifications/send-bulk', notification);
  }
}

